import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export interface SegmentedOption<T extends string> {
  id: T
  label: string
  suffix?: string
}

interface Props<T extends string> {
  options: ReadonlyArray<SegmentedOption<T>>
  value: T
  onChange: (value: T) => void
  size?: 'md' | 'lg'
  tone?: 'light' | 'dark'
  ariaLabel?: string
  className?: string
}

/**
 * Pill switcher avec indicateur animé (port du composant Next /pricing).
 * Utilisé dans Subscribe.tsx pour les toggles vue/facturation/devise.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  tone = 'dark',
  ariaLabel,
  className,
}: Props<T>) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 })

  useEffect(() => {
    if (!ref.current) return
    const idx = options.findIndex((o) => o.id === value)
    const buttons = ref.current.querySelectorAll<HTMLButtonElement>('[data-seg]')
    const el = buttons[idx]
    if (el) {
      const r = el.getBoundingClientRect()
      const p = ref.current.getBoundingClientRect()
      setIndicator({ left: r.left - p.left, width: r.width })
    }
  }, [value, options])

  const padY = size === 'lg' ? 'py-2.5' : 'py-2'
  const padX = size === 'lg' ? 'px-5' : 'px-4'
  const fs = size === 'lg' ? 'text-[15px]' : 'text-[14px]'
  const isDark = tone === 'dark'

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex p-1 rounded-full border',
        isDark
          ? 'bg-white/5 border-white/10 backdrop-blur'
          : 'bg-slate-100 border-slate-200',
        className,
      )}
    >
      <div
        className={cn(
          'absolute top-1 bottom-1 rounded-full shadow-sm transition-[left,width] duration-[260ms]',
          isDark ? 'bg-white' : 'bg-white border border-slate-200',
        )}
        style={{
          left: indicator.left,
          width: indicator.width,
          transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
        }}
      />
      {options.map((o) => {
        const active = value === o.id
        return (
          <button
            key={o.id}
            data-seg
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.id)}
            className={cn(
              'relative z-10 rounded-full font-medium transition-colors',
              padX,
              padY,
              fs,
              active
                ? isDark
                  ? 'text-[#0A192F]'
                  : 'text-slate-900'
                : isDark
                  ? 'text-white/70 hover:text-white'
                  : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {o.label}
            {o.suffix && (
              <span
                className={cn(
                  'ml-1 text-[12px] font-semibold',
                  active ? 'text-blue-600' : isDark ? 'text-blue-300' : 'text-blue-600',
                )}
              >
                {o.suffix}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
